import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import { Pencil, Map as MapIcon } from 'lucide-react'
import { useAppContext } from '@/hooks/routing'
import { useAircraftById } from '@/hooks/useAircraft'
import type { AircraftRegistryRow } from '@/types/aircrafts'
import {
  AIRCRAFT_STATUS_LABELS,
  AIRCRAFT_STATUS_VARIANTS,
  RELATIONSHIP_LABELS,
  PRESENCE_OPTIONS,
  getCategoryIconGroup,
  type AircraftRelationship,
} from '@/types/aircrafts'

interface AircraftDetailPanelProps {
  /** Direct data from registry table — no fetch needed */
  aircraft?: AircraftRegistryRow | null
  /** Fetch by ID — used when opened from another context (e.g. client detail panel) */
  aircraftId?: string
  open: boolean
  onOpenChange: (open: boolean) => void
  onEdit: (id: string) => void
}

function InfoRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-4 py-1.5">
      <span className="text-sm text-muted-foreground shrink-0">{label}</span>
      <span className="text-sm text-right font-medium">{value ?? <span className="text-muted-foreground font-normal">—</span>}</span>
    </div>
  )
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
        {title}
      </h3>
      {children}
    </div>
  )
}

export function AircraftDetailPanel({
  aircraft: aircraftProp,
  aircraftId,
  open,
  onOpenChange,
  onEdit,
}: AircraftDetailPanelProps) {
  const navigate = useNavigate()
  const { locationSlug } = useAppContext()

  // Fetch by ID when no direct data provided
  const fetchedQuery = useAircraftById(open && !aircraftProp && aircraftId ? aircraftId : undefined)

  // Map AircraftTableRow → AircraftRegistryRow with null defaults for registry-specific fields
  const aircraft = useMemo((): AircraftRegistryRow | null => {
    if (aircraftProp) return aircraftProp
    if (!fetchedQuery.data) return null
    const row = fetchedQuery.data
    return {
      ...row,
      allocation_id: null,
      position_type: null,
      building_name: null,
      unit_name: null,
      arrived_at: null,
      scheduled_departure: null,
      on_field_status: null,
      presence: 'away' as const,
      crm_stage: 'unknown' as const,
      relationship: 'no_contract' as const,
      lease_contract_id: null,
      lease_contract_number: null,
      lease_end_date: null,
      lease_monthly_rate: null,
      transient_stay_id: null,
      transient_booking_number: null,
      days_on_field: null,
      revenue_mtd: null,
      revenue_ytd: null,
      ar_balance: null,
      visits_12mo: null,
      avg_stay_days: null,
      last_visit_date: null,
      fuel_purchased_ytd: null,
      anomalies: [],
    }
  }, [aircraftProp, fetchedQuery.data])

  if (!aircraft) return null

  const typeName = aircraft.aircraft_type_name ||
    (aircraft.manufacturer && aircraft.model
      ? `${aircraft.manufacturer} ${aircraft.model}`
      : null)

  const categoryGroup = getCategoryIconGroup(aircraft.aircraft_type_category ?? null)

  const presenceOption = PRESENCE_OPTIONS.find((o) => o.value === aircraft.presence)
  const relationshipLabel = RELATIONSHIP_LABELS[aircraft.relationship as AircraftRelationship] ?? aircraft.relationship

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="sm:max-w-lg overflow-y-auto">
        <SheetHeader>
          <div className="flex items-start justify-between gap-2 pr-8">
            <div>
              <SheetTitle className="text-lg font-mono">
                {aircraft.tail_number}
              </SheetTitle>
              <SheetDescription>
                {typeName ?? 'Unknown Type'}
                {categoryGroup !== 'unknown' && (
                  <span className="ml-2 text-xs uppercase">({categoryGroup})</span>
                )}
              </SheetDescription>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {locationSlug && aircraft.building_name && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    navigate(`/${locationSlug}/ramp?aircraft=${aircraft.id}`)
                    onOpenChange(false)
                  }}
                >
                  <MapIcon className="mr-1.5 h-3.5 w-3.5" />
                  Map
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  onEdit(aircraft.id)
                  onOpenChange(false)
                }}
              >
                <Pencil className="mr-1.5 h-3.5 w-3.5" />
                Edit
              </Button>
            </div>
          </div>

          {/* Status badges */}
          <div className="flex items-center gap-2 mt-2 flex-wrap">
            <Badge variant={AIRCRAFT_STATUS_VARIANTS[aircraft.status]}>
              {AIRCRAFT_STATUS_LABELS[aircraft.status]}
            </Badge>
            {presenceOption && (
              <Badge variant="outline" className="text-xs">
                {presenceOption.icon && <span className="mr-1">{presenceOption.icon}</span>}
                {presenceOption.label}
              </Badge>
            )}
            {relationshipLabel && (
              <Badge variant="secondary" className="text-xs">
                {relationshipLabel}
              </Badge>
            )}
          </div>
        </SheetHeader>

        <div className="px-4 pb-6 space-y-5">
          <Separator />

          {/* Aircraft Specs */}
          <Section title="Aircraft Specs">
            <InfoRow label="Manufacturer" value={aircraft.aircraft_type_manufacturer ?? aircraft.manufacturer} />
            <InfoRow label="Model" value={aircraft.aircraft_type_model ?? aircraft.model} />
            <InfoRow label="Year" value={aircraft.year_manufactured} />
            <InfoRow label="Serial #" value={aircraft.serial_number} />
            <InfoRow label="Registration" value={aircraft.registration_country} />
            {(aircraft.wingspan || aircraft.length || aircraft.height) && (
              <>
                <InfoRow
                  label="Dimensions"
                  value={
                    [
                      aircraft.wingspan && `${aircraft.wingspan}ft WS`,
                      aircraft.length && `${aircraft.length}ft L`,
                      aircraft.height && `${aircraft.height}ft H`,
                    ]
                      .filter(Boolean)
                      .join(' / ') || null
                  }
                />
              </>
            )}
            {aircraft.max_takeoff_weight && (
              <InfoRow label="MTOW" value={`${aircraft.max_takeoff_weight.toLocaleString()} lbs`} />
            )}
          </Section>

          <Separator />

          {/* Owner & Operator */}
          <Section title="Owner & Operator">
            <InfoRow label="Owner" value={aircraft.owner_client_name ?? aircraft.owner_name} />
            {aircraft.operator_client_name && aircraft.operator_client_name !== aircraft.owner_client_name && (
              <InfoRow label="Operator" value={aircraft.operator_client_name} />
            )}
          </Section>

          <Separator />

          {/* Active Contract */}
          <Section title="Contract">
            {aircraft.lease_contract_number ? (
              <>
                <InfoRow label="Contract #" value={aircraft.lease_contract_number} />
                <InfoRow
                  label="Lease End"
                  value={
                    aircraft.lease_end_date
                      ? new Date(aircraft.lease_end_date).toLocaleDateString()
                      : null
                  }
                />
                <InfoRow
                  label="Monthly Rate"
                  value={
                    aircraft.lease_monthly_rate != null
                      ? `$${aircraft.lease_monthly_rate.toLocaleString()}`
                      : null
                  }
                />
              </>
            ) : aircraft.transient_booking_number ? (
              <InfoRow label="Booking #" value={aircraft.transient_booking_number} />
            ) : (
              <p className="text-sm text-muted-foreground">No active contract</p>
            )}
          </Section>

          <Separator />

          {/* Location */}
          <Section title="Position">
            <InfoRow label="Home Location" value={aircraft.home_location_name} />
            <InfoRow
              label="Current Position"
              value={
                aircraft.building_name
                  ? aircraft.unit_name
                    ? `${aircraft.building_name} / ${aircraft.unit_name}`
                    : aircraft.building_name
                  : null
              }
            />
            {aircraft.arrived_at && (
              <InfoRow label="Arrived" value={new Date(aircraft.arrived_at).toLocaleDateString()} />
            )}
            {aircraft.scheduled_departure && (
              <InfoRow label="ETD" value={new Date(aircraft.scheduled_departure).toLocaleDateString()} />
            )}
            {aircraft.days_on_field != null && (
              <InfoRow label="Days on Field" value={`${aircraft.days_on_field}d`} />
            )}
          </Section>

          <Separator />

          {/* Compliance */}
          <Section title="Compliance">
            <InfoRow
              label="Insurance Expiry"
              value={
                aircraft.insurance_expiry ? (
                  <InsuranceExpiry date={aircraft.insurance_expiry} />
                ) : null
              }
            />
            <InfoRow
              label="Annual Inspection"
              value={
                aircraft.annual_inspection_due
                  ? new Date(aircraft.annual_inspection_due).toLocaleDateString()
                  : null
              }
            />
          </Section>

          <Separator />

          {/* Financial Snapshot — Phase 2 placeholder */}
          <Section title="Financial Snapshot">
            <p className="text-sm text-muted-foreground">—</p>
          </Section>

          <Separator />

          {/* History — Phase 2 placeholder */}
          <Section title="Position History">
            <p className="text-sm text-muted-foreground italic">Coming soon</p>
          </Section>

          <Section title="Visit History">
            <p className="text-sm text-muted-foreground italic">Coming soon</p>
          </Section>
        </div>
      </SheetContent>
    </Sheet>
  )
}

function InsuranceExpiry({ date }: { date: string }) {
  const expiryDate = new Date(date)
  const now = new Date()
  const daysUntil = Math.ceil((expiryDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))

  const isExpired = daysUntil < 0
  const isExpiringSoon = daysUntil >= 0 && daysUntil <= 30

  return (
    <span
      className={
        isExpired
          ? 'text-destructive font-medium'
          : isExpiringSoon
            ? 'text-yellow-600 dark:text-yellow-500 font-medium'
            : ''
      }
    >
      {expiryDate.toLocaleDateString()}
      {isExpired && ' (Expired)'}
      {isExpiringSoon && !isExpired && ` (${daysUntil}d)`}
    </span>
  )
}
